import {
  Activity,
  AlertTriangle,
  ArrowRight,
  CheckCircle,
  Clock,
  Database,
  HardDrive,
  Loader2,
  Play,
  Timer,
  Users,
  XCircle,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AppShell } from '@/components/layout/AppShell'
import { useAuth } from '@/hooks/use-auth'
import { cn, timeAgo } from '@/lib/utils'
import { useSystemStore } from '@/stores/system-store'

function StatusDot({ ok }: { ok: boolean }) {
  return (
    <span className={cn('inline-block h-2 w-2 rounded-full', ok ? 'bg-positive' : 'bg-negative')} />
  )
}

export function SystemOverview() {
  useAuth()
  const {
    healthMetrics,
    isLoadingHealth,
    cronStats,
    isLoadingCronStats,
    triggeringCron,
    fetchHealthMetrics,
    fetchCronStats,
    triggerCron,
  } = useSystemStore()

  const [confirmTrigger, setConfirmTrigger] = useState<string | null>(null)
  const [triggerResult, setTriggerResult] = useState<{ cronName: string; success: boolean } | null>(
    null
  )

  useEffect(() => {
    fetchHealthMetrics()
    fetchCronStats()
  }, [fetchHealthMetrics, fetchCronStats])

  const isLoading = (isLoadingHealth && !healthMetrics) || (isLoadingCronStats && !cronStats)

  return (
    <AppShell title="System">
      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <p className="font-mono text-xs text-ink-faint animate-pulse">
            Loading system metrics...
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Service Status */}
          <div className="rounded-lg border border-dashed border-border bg-surface p-5">
            <p className="mb-4 font-mono text-[11px] tracking-wider text-ink-faint uppercase">
              Service Status
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="flex items-center gap-3 rounded-md border border-dashed border-border bg-canvas p-4">
                <Database className="h-5 w-5 text-accent" strokeWidth={1.5} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-mono text-xs font-medium text-ink">Database</p>
                    <StatusDot ok={healthMetrics?.database.status === 'connected'} />
                  </div>
                  <p className="font-mono text-[10px] text-ink-faint">
                    {healthMetrics?.database.latencyMs ?? '—'}ms latency
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 rounded-md border border-dashed border-border bg-canvas p-4">
                <HardDrive className="h-5 w-5 text-accent" strokeWidth={1.5} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-mono text-xs font-medium text-ink">R2 Storage</p>
                    <StatusDot ok={healthMetrics?.storage.status === 'connected'} />
                  </div>
                  <p className="font-mono text-[10px] text-ink-faint">
                    {healthMetrics?.storage.status ?? 'unknown'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 rounded-md border border-dashed border-border bg-canvas p-4">
                <Activity className="h-5 w-5 text-accent" strokeWidth={1.5} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-mono text-xs font-medium text-ink">API</p>
                    <StatusDot ok={(healthMetrics?.api.errorRate24h ?? 0) < 10} />
                  </div>
                  <p className="font-mono text-[10px] text-ink-faint">
                    {healthMetrics?.api.errorRate24h ?? 0}% error rate (24h)
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="rounded-lg border border-dashed border-border bg-surface p-5">
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-3.5 w-3.5 text-ink-faint" strokeWidth={1.5} />
                <p className="font-mono text-[11px] tracking-wider text-ink-faint uppercase">
                  Total Users
                </p>
              </div>
              <p className="font-mono text-2xl font-semibold tabular-nums text-ink">
                {healthMetrics?.users.total ?? 0}
              </p>
              <p className="mt-1 font-mono text-[10px] text-ink-faint">
                +{healthMetrics?.users.new24h ?? 0} today · +{healthMetrics?.users.new7d ?? 0} this
                week
              </p>
            </div>

            <div className="rounded-lg border border-dashed border-border bg-surface p-5">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="h-3.5 w-3.5 text-ink-faint" strokeWidth={1.5} />
                <p className="font-mono text-[11px] tracking-wider text-ink-faint uppercase">
                  Requests (24h)
                </p>
              </div>
              <p className="font-mono text-2xl font-semibold tabular-nums text-ink">
                {healthMetrics?.api.requests24h ?? 0}
              </p>
              <p className="mt-1 font-mono text-[10px] text-ink-faint">
                avg {healthMetrics?.api.avgResponseTimeMs ?? 0}ms response
              </p>
            </div>

            <div className="rounded-lg border border-dashed border-border bg-surface p-5">
              <div className="flex items-center gap-2 mb-2">
                <Timer className="h-3.5 w-3.5 text-ink-faint" strokeWidth={1.5} />
                <p className="font-mono text-[11px] tracking-wider text-ink-faint uppercase">
                  Cron (24h)
                </p>
              </div>
              <p className="font-mono text-2xl font-semibold tabular-nums text-ink">
                {healthMetrics?.cron.executions24h ?? 0}
              </p>
              <p className="mt-1 font-mono text-[10px] text-ink-faint">
                {healthMetrics?.cron.failures24h ?? 0} failures
              </p>
            </div>

            <div className="rounded-lg border border-dashed border-border bg-surface p-5">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-3.5 w-3.5 text-ink-faint" strokeWidth={1.5} />
                <p className="font-mono text-[11px] tracking-wider text-ink-faint uppercase">
                  Waitlist
                </p>
              </div>
              <p className="font-mono text-2xl font-semibold tabular-nums text-ink">
                {healthMetrics?.waitlist.pending ?? 0}
              </p>
              <p className="mt-1 font-mono text-[10px] text-ink-faint">pending reviews</p>
            </div>
          </div>

          {/* Cron Job Health */}
          <div className="rounded-lg border border-dashed border-border bg-surface p-5">
            <p className="mb-4 font-mono text-[11px] tracking-wider text-ink-faint uppercase">
              Cron Job Health
            </p>

            {cronStats?.jobs && cronStats.jobs.length > 0 ? (
              <div className="space-y-3">
                {cronStats.jobs.map((job) => (
                  <div
                    key={job.cronName}
                    className="rounded-md border border-dashed border-border bg-canvas p-4"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <StatusDot ok={job.successRate >= 90} />
                        <p className="font-mono text-xs font-medium text-ink">
                          {job.cronName.replace(/_/g, ' ')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={cn(
                            'rounded-full px-2 py-0.5 font-mono text-[10px]',
                            job.successRate >= 95
                              ? 'bg-positive/10 text-positive'
                              : job.successRate >= 80
                                ? 'bg-warning/10 text-warning'
                                : 'bg-negative/10 text-negative'
                          )}
                        >
                          {job.successRate}% success
                        </span>
                        {confirmTrigger === job.cronName ? (
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={async () => {
                                const result = await triggerCron(job.cronName)
                                setConfirmTrigger(null)
                                setTriggerResult({
                                  cronName: job.cronName,
                                  success: result.success,
                                })
                                setTimeout(() => setTriggerResult(null), 3000)
                              }}
                              disabled={triggeringCron === job.cronName}
                              className="flex items-center gap-1 rounded-full bg-accent px-2.5 py-0.5 font-mono text-[10px] font-medium text-white transition-colors hover:bg-accent/80 disabled:opacity-50"
                            >
                              {triggeringCron === job.cronName ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Play className="h-3 w-3" />
                              )}
                              Confirm
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmTrigger(null)}
                              className="rounded-full border border-border px-2 py-0.5 font-mono text-[10px] text-ink-faint transition-colors hover:bg-surface-alt"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setConfirmTrigger(job.cronName)}
                            disabled={triggeringCron !== null}
                            className="flex items-center gap-1 rounded-full border border-border px-2 py-0.5 font-mono text-[10px] text-ink-faint transition-colors hover:border-accent hover:text-accent disabled:opacity-50"
                          >
                            <Play className="h-2.5 w-2.5" />
                            Run Now
                          </button>
                        )}
                        {triggerResult?.cronName === job.cronName && (
                          <span
                            className={cn(
                              'font-mono text-[10px]',
                              triggerResult.success ? 'text-positive' : 'text-negative'
                            )}
                          >
                            {triggerResult.success ? 'Done' : 'Failed'}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-4">
                      <div>
                        <p className="font-mono text-[10px] text-ink-faint">Total Runs</p>
                        <p className="font-mono text-sm tabular-nums text-ink">{job.totalRuns}</p>
                      </div>
                      <div>
                        <p className="font-mono text-[10px] text-ink-faint">Avg Duration</p>
                        <p className="font-mono text-sm tabular-nums text-ink">
                          {job.avgDurationMs}ms
                        </p>
                      </div>
                      <div>
                        <p className="font-mono text-[10px] text-ink-faint">Failures</p>
                        <p className="font-mono text-sm tabular-nums text-ink">
                          {job.failureCount}
                        </p>
                      </div>
                      <div>
                        <p className="font-mono text-[10px] text-ink-faint">Last Run</p>
                        <p className="font-mono text-sm text-ink">
                          {job.lastRun ? timeAgo(job.lastRun) : '—'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="py-6 text-center font-mono text-xs text-ink-faint">
                No cron execution data yet. Logs will appear after the next hourly run.
              </p>
            )}
          </div>

          {/* Recent Cron Failures */}
          {cronStats?.recentFailures && cronStats.recentFailures.length > 0 && (
            <div className="rounded-lg border border-dashed border-negative/30 bg-negative/5 p-5">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="h-4 w-4 text-negative" strokeWidth={1.5} />
                <p className="font-mono text-[11px] tracking-wider text-negative uppercase">
                  Recent Failures (7d)
                </p>
              </div>
              <div className="space-y-2">
                {cronStats.recentFailures.map((failure) => (
                  <div
                    key={failure.id}
                    className="flex items-start gap-3 rounded-md border border-dashed border-negative/20 bg-canvas p-3"
                  >
                    <XCircle
                      className="mt-0.5 h-3.5 w-3.5 shrink-0 text-negative"
                      strokeWidth={1.5}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-mono text-[11px] font-medium text-ink">
                          {failure.cronName.replace(/_/g, ' ')}
                        </p>
                        <span className="font-mono text-[10px] text-ink-faint">
                          {timeAgo(failure.startedAt)}
                        </span>
                      </div>
                      {failure.errors && (
                        <p className="mt-1 truncate font-mono text-[10px] text-negative">
                          {failure.errors}
                        </p>
                      )}
                    </div>
                    <p className="shrink-0 font-mono text-[10px] tabular-nums text-ink-faint">
                      {failure.durationMs}ms
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Successes indicator */}
          {cronStats?.recentFailures?.length === 0 && cronStats.jobs.length > 0 && (
            <div className="rounded-lg border border-dashed border-positive/30 bg-positive/5 p-5">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-positive" strokeWidth={1.5} />
                <p className="font-mono text-[11px] text-positive">
                  No cron failures in the last 7 days
                </p>
              </div>
            </div>
          )}

          {/* Navigation */}
          <Link
            to="/system/cron"
            className="group flex items-center justify-between rounded-lg border border-dashed border-border bg-surface p-5 transition-colors hover:bg-surface-alt"
          >
            <div className="flex items-center gap-3">
              <Timer className="h-5 w-5 text-accent" strokeWidth={1.5} />
              <div>
                <p className="font-mono text-xs font-medium text-ink">Cron Execution Log</p>
                <p className="font-mono text-[10px] text-ink-faint">
                  Browse full history of all cron job executions
                </p>
              </div>
            </div>
            <ArrowRight className="h-4 w-4 text-ink-faint transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      )}
    </AppShell>
  )
}
